import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { z } from 'zod/v4';
import { intervalSizeLevelsSchema, queryOptionsSchema } from './query-options';
import { savedViewsSchema } from './saved-view';
import { durationSchema, timeDashboardSchema } from './time';
import { variableValueSchema } from './variables';

// This adds the openapi function and the ability to generate OpenAPI.
// Should only be called once, ideally we limit openapi usage to these helpers
// since users don't need to know about it.
// https://github.com/chronosphereio/monorepo/pull/63567#discussion_r2025672899
extendZodWithOpenApi(z);

/**
 * Create a schema for a zod type that gets registered in the OpenAPI spec.
 * This is specifically useful when you want a schema to be given a name, e.g.
 * it is reused in multiple places, in the OpenAPI docs site.
 */
export const createNamedSchema = <T extends z.ZodTypeAny>(name: string, spec: T) => {
  return spec.openapi(name, {
    title: name,
  });
};

/**
 * Create a definition schema for a specific kind of definition. This is a common pattern
 * to differentiate between different kinds of objects in a oneOf.
 */
export const createDefinitionSchema = <Spec extends z.ZodTypeAny, Kind extends string>(kind: Kind, spec: Spec) => {
  const s = z.object({
    kind: z.literal(kind),
    spec,
  });
  return s.openapi(kind, {
    title: kind,
  });
};

/**
 * Creates a schema for an unknown definition.
 */
const createUnknownDefinitionSchema = () => {
  return z.object({
    kind: z.string(),
    spec: z.record(z.string(), z.any()),
  });
};

export const unknownDefinitionSchema = createUnknownDefinitionSchema();
export type UnknownDefinition = z.infer<typeof unknownDefinitionSchema>;

/*
 * Creates a schema for a formula definition.
 */
export type FormulaDefinition = z.infer<typeof formulaDefinition>;
const formulaDefinition = createDefinitionSchema(
  'Formula',
  z.object({
    // expression represents a math formula that references other queries. Formulas must use the
    // queries alpha, e.g. as seen in indexToAlpha, to reference the query. For example,
    // (a / b) * 100
    expression: z.string().describe('A math expression that references other queries, e.g. (a / b) * 100'),
    alias: z.string().describe('Optional alias of the formula').optional(),
    disabled: z.boolean().optional(),
  })
);

/**
 * Creates a schema for a panel definition.
 */
export const createPanelDefinitionSchema = <
  PanelPluginDefinition extends z.ZodTypeAny,
  DataQueryPluginDefinition extends z.ZodTypeAny,
>(
  pluginDefinition: PanelPluginDefinition,
  dataQueryDefinition: DataQueryPluginDefinition
) => {
  return createDefinitionSchema(
    'Panel',
    z.object({
      display: panelDisplaySchema,
      plugin: pluginDefinition,
      formulas: z.array(formulaDefinition).optional(),
      queries: z.array(createDataQueryPluginDefinitionSchema(dataQueryDefinition)).optional(),
      query_options: queryOptionsSchema.optional(),
      links: z.array(linksSchema).optional(),
    })
  );
};

const linksSchema = createDefinitionSchema('ContextLink', z.object({ title: z.string(), url: z.string() }));

/**
 * Creates a schema for a data query definition.
 */
const createDataQueryPluginDefinitionSchema = <DataQueryPluginDefinition extends z.ZodTypeAny>(
  pluginSpec: DataQueryPluginDefinition
) => {
  return createDefinitionSchema(
    'DataQuery',
    z.object({
      id: z.string().optional(),
      alias: z.string().optional(),
      disabled: z.boolean().optional(),
      plugin: pluginSpec,
    })
  );
};

const displaySchema = z.object({
  name: z.string(),
  description: z.string().optional(),
});

/**
 * Creates a schema for a panel display definition.
 */
export type PanelDisplay = z.infer<typeof panelDisplaySchema>;
const panelDisplaySchema = displaySchema.extend({
  // name for panels is optional.
  name: z.string().optional(),
});

const contentDimensionsSchema = z.object({
  width: z.number(),
  height: z.number(),
});
export type ContentDimensions = z.infer<typeof contentDimensionsSchema>;

const panelRefSchema = z.object({
  $ref: z.string(),
});
export type PanelRef = z.infer<typeof panelRefSchema>;

const gridItemSchema = contentDimensionsSchema.extend({
  x: z.number(),
  y: z.number(),
  content: panelRefSchema,
});

export type GridItemDefinition = z.infer<typeof gridItemSchema>;

const panelGroupDisplaySchema = z
  .object({
    title: z.string(),
    collapse: z
      .object({
        open: z.boolean(),
      })
      .optional(),
  })
  .optional();

const gridDefinitionSchema = createDefinitionSchema(
  'Grid',
  z.object({
    display: panelGroupDisplaySchema,
    split_by: z
      .object({
        variable: z.string(),
      })
      .optional(),
    items: z.array(gridItemSchema),
  })
);
export type GridDefinition = z.infer<typeof gridDefinitionSchema>;

const splitPanelDefinitionSchema = createDefinitionSchema(
  'SplitPanel',
  z.object({
    display: panelGroupDisplaySchema,
    split_by: z
      .object({
        variable: z.string(),
      })
      .optional(),
    content: panelRefSchema,
  })
);
export type SplitPanelDefinition = z.infer<typeof splitPanelDefinitionSchema>;

export const layoutDefinitionSchema = z.discriminatedUnion('kind', [gridDefinitionSchema, splitPanelDefinitionSchema]);
export type LayoutDefinition = z.infer<typeof layoutDefinitionSchema>;
/**
 * Creates a schema for an event definition.
 */
function createEventSchema<QueryEventDefinition extends z.ZodTypeAny>(queryEventSchema: QueryEventDefinition) {
  return createDefinitionSchema(
    'QueryEvent',
    z.object({
      name: z.string(),
      display: displaySchema
        .extend({
          hidden: z.boolean().optional(),
        })
        .optional(),
      enabled: z.boolean().optional(),
      plugin: queryEventSchema,
    })
  );
}

export const commonVariableSpec = z.object({
  name: z.string(),
  display: displaySchema
    .extend({
      hidden: z.boolean().optional(),
    })
    .optional(),
});

export const textVariableSchema = createDefinitionSchema(
  'TextVariable',
  commonVariableSpec.extend({
    value: z.string(),
  })
);
export type TextVariableDefinition = z.infer<typeof textVariableSchema>;

export const intervalVariableSchema = createDefinitionSchema(
  'IntervalVariable',
  commonVariableSpec.extend({
    default_value: z.string().optional(),
    options: z.array(z.string()).optional().describe('The list of interval options'),
    auto: z.boolean().optional().describe('Whether to include the the auto option'),
    auto_min_interval: z.string().optional().describe('The minimum interval to use when auto is enabled'),
    auto_interval_size: intervalSizeLevelsSchema
      .optional()
      .describe('The interval size to use when auto is enabled (Small, Medium, Large)'),
  })
);
export type IntervalVariableDefinition = z.infer<typeof intervalVariableSchema>;
export type IntervalVariableSpec = IntervalVariableDefinition['spec'];

/**
 * Creates a schema for a list variable definition.
 */
export function createListVariableSchema<PluginDefinition extends z.ZodTypeAny>(pluginDefinition: PluginDefinition) {
  return createDefinitionSchema(
    'ListVariable',
    commonVariableSpec.extend({
      plugin: pluginDefinition,
      default_value: variableValueSchema.optional(),
      allow_multiple: z.boolean().optional(),
      allow_all_value: z.boolean().optional(),
      custom_all_value: z.string().optional(),
      capturing_regexp: z.string().optional(),
      refresh: z.enum(['DashboardLoad', 'TimeRangeUpdate']).optional(),
      sort: z.enum(['AlphabeticalAsc', 'AlphabeticalDesc', 'NumericalAsc', 'NumericalDesc']).optional(),
    })
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const unknownListVariablePluginDefinitionSchema = createListVariableSchema(createUnknownDefinitionSchema());
export type ListVariableDefinition = z.infer<typeof unknownListVariablePluginDefinitionSchema>;

/**
 * Creates a schema for a variable definition.
 */
function createVariableSchema<PluginDefinition extends z.ZodTypeAny>(pluginDefinition: PluginDefinition) {
  return z.discriminatedUnion('kind', [
    textVariableSchema,
    createListVariableSchema(pluginDefinition),
    intervalVariableSchema,
  ]);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const unknownVariableDefinitionSchema = createVariableSchema(unknownDefinitionSchema);
export type VariableDefinition = z.infer<typeof unknownVariableDefinitionSchema>;

/**
 * Creates a schema for a dashboard definition.
 */
export const createDashboardDefinitionSchema = <
  PanelPluginDefinition extends z.ZodTypeAny,
  DataQueryPluginDefinition extends z.ZodTypeAny,
  EventPluginDefinition extends z.ZodTypeAny,
  ListVariablePluginDefinition extends z.ZodTypeAny,
>(args: {
  panelPluginDefinitionSchema: PanelPluginDefinition;
  dataQueryPluginDefinitionSchema: DataQueryPluginDefinition;
  eventPluginDefinitionSchema: EventPluginDefinition;
  listVariablePluginDefinitionSchema: ListVariablePluginDefinition;
}) => {
  const { panelPluginDefinitionSchema, dataQueryPluginDefinitionSchema, eventPluginDefinitionSchema } = args;
  const eventDefinitionSchema = createEventSchema(eventPluginDefinitionSchema);
  const panelDefinitionSchema = createPanelDefinitionSchema(
    panelPluginDefinitionSchema,
    dataQueryPluginDefinitionSchema
  );
  const variableDefinitionSchema = createVariableSchema(args.listVariablePluginDefinitionSchema);

  return z.object({
    kind: z.literal('Dashboard'),
    spec: z.object({
      variables: z.array(variableDefinitionSchema),
      panels: z.record(z.string(), panelDefinitionSchema),
      layouts: z.array(layoutDefinitionSchema),
      events: z.array(eventDefinitionSchema).optional(),
      duration: durationSchema.optional(),
      time: timeDashboardSchema.optional(),
      refreshInterval: durationSchema.describe('DEPRECATED').optional(),
      saved_views: savedViewsSchema.optional(),
    }),
    spec_version: z.string(),
  });
};

export const mockDataQueryDefinitionSchema = createDefinitionSchema('MockDataQuery', z.record(z.string(), z.any()));

export const genericDashboardSchema = createDashboardDefinitionSchema({
  dataQueryPluginDefinitionSchema: unknownDefinitionSchema,
  panelPluginDefinitionSchema: unknownDefinitionSchema,
  eventPluginDefinitionSchema: unknownDefinitionSchema,
  listVariablePluginDefinitionSchema: unknownDefinitionSchema,
});

export type GenericDashboardDefinition = z.infer<typeof genericDashboardSchema>;
